import React from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const StyledSegmentedToggle = styled.div({
    height: tokens.globals["spacing-9"],
    padding: tokens.globals["spacing-1"],
    background: tokens.colors.theme["sidebar-color"],
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
    display: "inline-flex",
    alignItems: "center",
    gap: 0,
    flexShrink: 0,
});

const activeOptionOverlay = "rgba(248, 248, 252, 0.2)";

const StyledSegmentedOption = styled.button(({ $active = false }) => ({
    height: tokens.globals["spacing-7"],
    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,
    borderRadius: "8px",
    border: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: $active ? activeOptionOverlay : "transparent",
    color: tokens.colors.neutral.white,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    transition: "background 0.15s ease-in-out",

    ...($active && {
        "&:hover:not(:disabled)": {
            background: activeOptionOverlay,
        },
    }),
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.neutral.white}`,
        outlineOffset: tokens.globals["spacing-px"],
    },
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
}));

/**
 * Pill-shaped segmented toggle, e.g. "Standard View | QA View" or "Study Phases | Re-arrange".
 * Used by StudyPhasesToggle and Programs.js's Standard/QA View toggle. Reach for this when a screen
 * has two (or a few) mutually-exclusive views of the same underlying data, rather than separate
 * tabs or routes.
 * @param {{value: string, label: React.ReactNode, disabled?: boolean, disabledReason?: string}[]} props.options
 */
function SegmentedToggle({ value, onChange, options, ariaLabel }) {
    return (
        <StyledSegmentedToggle role="tablist" aria-label={ariaLabel}>
            {options.map((option) => (
                <StyledSegmentedOption
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={value === option.value}
                    $active={value === option.value}
                    onClick={() => onChange(option.value)}
                    disabled={option.disabled}
                    title={option.disabled ? option.disabledReason : undefined}
                >
                    {option.label}
                </StyledSegmentedOption>
            ))}
        </StyledSegmentedToggle>
    );
}

SegmentedToggle.displayName = "SegmentedToggle";

export default SegmentedToggle;
